import { auth } from '../firebase';
import type {
  EntityUser,
  SessionConfig,
  Project,
  ProjectLink,
  ProjectMember,
  IntegrationResource,
  Ticket,
  RiskCache,
  DailyInsight,
  DailyBriefing,
  BriefingRefreshResult,
  Integration,
  PersonalIntegration,
  Invite,
  CompanySettings,
  AiKeyConfig,
  StatusUpdateRequest,
  EntityRole,
  FunctionalRole,
  Goal,
  LinkedKr,
  ActionResult,
} from '../types';

export const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await auth.currentUser?.getIdToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...(options.headers ?? {}),
    },
  });

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = (await res.json()) as { detail?: unknown; message?: string };
      const detail = body.detail;
      message = (typeof detail === 'string' ? detail : (detail as Record<string, string>)?.message) ?? body.message ?? message;
    } catch {
      // ignore parse error
    }
    const err = new Error(message);
    (err as Error & { status: number }).status = res.status;
    throw err;
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const envelope = await res.json() as { data: T };
  return envelope.data;
}

// Session
export function getSessionConfig(): Promise<SessionConfig> {
  return request<SessionConfig>('/session/config');
}

// Users
export function getMe(): Promise<EntityUser> {
  return request<EntityUser>('/users/me');
}

export function updateMe(data: Partial<EntityUser>): Promise<EntityUser> {
  return request<EntityUser>('/users/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function completeOnboarding(): Promise<EntityUser> {
  return request<EntityUser>('/users/me', {
    method: 'PUT',
    body: JSON.stringify({ onboardingCompletedAt: new Date().toISOString() }),
  });
}

export async function listUsers(params?: { page?: number; pageSize?: number }): Promise<EntityUser[]> {
  const qs = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';
  const res = await request<{ users: EntityUser[]; total: number }>(`/users${qs}`);
  return res.users;
}

export function updateUser(
  userId: string,
  data: { entityRole?: EntityRole; functionalRoles?: FunctionalRole[]; primaryRole?: FunctionalRole | null; department?: string; isActive?: boolean }
): Promise<EntityUser> {
  return request<EntityUser>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Invites
export function inviteUser(data: { email: string; entityRole: EntityRole; functionalRoles?: FunctionalRole[]; primaryRole?: FunctionalRole | null }): Promise<Invite> {
  return request<Invite>('/invites', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function listInvites(): Promise<Invite[]> {
  const res = await request<{ invites: Invite[] }>('/invites');
  return res.invites;
}

export function revokeInvite(id: string): Promise<void> {
  return request<void>(`/invites/${id}`, { method: 'DELETE' });
}

export function resendInvite(id: string): Promise<void> {
  return request<void>(`/invites/${id}/resend`, { method: 'POST' });
}

// Projects
export async function listProjects(params?: {
  status?: string;
  page?: number;
  pageSize?: number;
}): Promise<Project[]> {
  const qs = params
    ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
    : '';
  const res = await request<{ projects: Project[]; nextCursor: string | null; filterCounts: Record<string, number> }>(`/projects${qs}`);
  return res.projects;
}

export function getProject(id: string): Promise<Project> {
  return request<Project>(`/projects/${id}`);
}

export function createProject(data: {
  name: string;
  description?: string;
  status?: string;
}): Promise<Project> {
  return request<Project>('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateProject(
  id: string,
  data: Partial<Project>
): Promise<Project> {
  return request<Project>(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteProject(id: string): Promise<void> {
  return request<void>(`/projects/${id}`, { method: 'DELETE' });
}

export async function getProjectMembers(projectId: string): Promise<ProjectMember[]> {
  const res = await request<{ members: ProjectMember[] }>(`/projects/${projectId}/members`);
  return res.members;
}

export function addProjectMember(projectId: string, userId: string, role: string): Promise<ProjectMember> {
  return request<ProjectMember>(`/projects/${projectId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, role }),
  });
}

export function removeProjectMember(projectId: string, userId: string): Promise<void> {
  return request<void>(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' });
}

export async function getMyInvolvements(): Promise<string[]> {
  const res = await request<{ projectIds: string[] }>('/users/me/involvements');
  return res.projectIds;
}

// Tickets
export async function listTickets(
  projectId: string,
  params?: { status?: string; type?: string }
): Promise<Ticket[]> {
  const qs = `?${new URLSearchParams({ limit: '100', ...(params ?? {}) }).toString()}`;
  const res = await request<{ tickets: Ticket[]; nextCursor: string | null }>(`/projects/${projectId}/tickets${qs}`);
  return res.tickets;
}

export function createTicket(
  projectId: string,
  data: {
    title: string;
    description?: string;
    status?: string;
    type?: string;
    assigneeId?: string;
  }
): Promise<Ticket> {
  return request<Ticket>(`/projects/${projectId}/tickets`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateTicket(
  id: string,
  data: Partial<Ticket>
): Promise<Ticket> {
  return request<Ticket>(`/tickets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Insights
export async function getWhatsOnDeck(): Promise<DailyInsight | null> {
  const res = await request<{ insightCache: DailyInsight | null }>('/insights/whats-on-deck');
  return res.insightCache;
}

export async function refreshWhatsOnDeck(): Promise<void> {
  await request<{ queued: boolean; entityId: string }>('/insights/whats-on-deck/refresh', {
    method: 'POST',
  });
}

// Briefing
export async function getTodayBriefing(role: string): Promise<DailyBriefing | null> {
  const res = await request<{ briefingCache: DailyBriefing | null }>(
    `/briefing/today?role=${encodeURIComponent(role)}`
  );
  return res.briefingCache;
}

export async function refreshTodayBriefing(role: string): Promise<BriefingRefreshResult> {
  return request<BriefingRefreshResult>(
    `/briefing/today/refresh?role=${encodeURIComponent(role)}`,
    { method: 'POST' }
  );
}

// Marketing dashboard
export interface DateSlipEvent {
  projectId: string;
  projectName: string;
  previousDueDate: string;
  newDueDate: string;
  changedAt: string;
}

export async function getDateSlipFeed(): Promise<DateSlipEvent[]> {
  const res = await request<{ slips: DateSlipEvent[] }>('/projects/date-slips');
  return res.slips;
}

export interface MessagingCoverageEntry {
  projectId: string;
  projectName: string;
  hasMessagingAsset: boolean;
}

export async function getMessagingCoverage(): Promise<MessagingCoverageEntry[]> {
  const res = await request<{ projects: MessagingCoverageEntry[] }>('/projects/messaging-coverage');
  return res.projects;
}

// Risk
export async function getProjectRisk(projectId: string): Promise<RiskCache | null> {
  const res = await request<{ riskCache: RiskCache | null }>(`/projects/${projectId}/risk`);
  return res.riskCache;
}

export async function refreshProjectRisk(projectId: string): Promise<void> {
  await request<{ queued: boolean; projectId: string }>(`/projects/${projectId}/risk/refresh`, {
    method: 'POST',
  });
}

export interface RiskHistoryPoint {
  date: string;
  score: number;
  staleCount?: number | null;
  activeTicketCount?: number | null;
  trend?: string | null;
  confidence?: number | null;
}

export async function getRiskHistory(
  projectId: string,
  days?: number
): Promise<RiskHistoryPoint[]> {
  const qs = days ? `?days=${days}` : '';
  const res = await request<{ history: RiskHistoryPoint[] }>(
    `/projects/${projectId}/risk/history${qs}`
  );
  return res.history;
}

// Reports
export function generateStatusUpdate(
  projectId: string,
  data: StatusUpdateRequest
): Promise<{ format: string; audience: string; subject: string; body: string }> {
  return request<{ format: string; audience: string; subject: string; body: string }>(
    `/projects/${projectId}/reports/status-update`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
}

// Goals
export async function listGoals(): Promise<Goal[]> {
  const res = await request<{ goals: Goal[] }>('/goals');
  return res.goals;
}

export async function createGoal(data: Omit<Goal, 'goalId' | 'status' | 'createdAt' | 'updatedAt'>): Promise<Goal> {
  return request<Goal>('/goals', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateGoal(goalId: string, data: Partial<Goal>): Promise<Goal> {
  return request<Goal>(`/goals/${goalId}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteGoal(goalId: string): Promise<void> {
  return request<void>(`/goals/${goalId}`, { method: 'DELETE' });
}

export async function updateProjectLinkedGoals(projectId: string, linkedKrIds: LinkedKr[]): Promise<void> {
  return request<void>(`/projects/${projectId}/linked-goals`, {
    method: 'PUT',
    body: JSON.stringify({ linkedKrIds }),
  });
}

// Settings
export async function getSettings(): Promise<CompanySettings> {
  const res = await request<{ settings: CompanySettings }>('/settings');
  return res.settings;
}

export async function updateSettings(
  data: Partial<CompanySettings>
): Promise<CompanySettings> {
  const res = await request<{ settings: CompanySettings }>('/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return res.settings;
}

// BYOK (Bring Your Own Key) — Gemini API key. The key itself is write-only
// from the client's perspective: setGeminiByokKey sends it once to be
// tested and stored, every response (including this one's return value)
// carries at most a masked last-4 suffix, never the key.
export async function setGeminiByokKey(apiKey: string): Promise<AiKeyConfig> {
  const res = await request<{ aiKeyConfig: AiKeyConfig }>('/settings/ai-key', {
    method: 'POST',
    body: JSON.stringify({ apiKey }),
  });
  return res.aiKeyConfig;
}

export async function toggleByok(useByok: boolean): Promise<AiKeyConfig> {
  const res = await request<{ aiKeyConfig: AiKeyConfig }>('/settings/ai-key/toggle', {
    method: 'PUT',
    body: JSON.stringify({ useByok }),
  });
  return res.aiKeyConfig;
}

export async function deleteGeminiByokKey(): Promise<AiKeyConfig> {
  const res = await request<{ aiKeyConfig: AiKeyConfig }>('/settings/ai-key', {
    method: 'DELETE',
  });
  return res.aiKeyConfig;
}

// Integrations
export async function listIntegrations(): Promise<Integration[]> {
  const res = await request<{ integrations: Integration[] }>('/integrations');
  return res.integrations;
}

export type IntegrationHealthStatus = 'healthy' | 'paused' | 'stale' | 'unknown';

export interface IntegrationHealthEntry {
  status: IntegrationHealthStatus;
  checkedAt: string | null;
}

export type IntegrationHealthMap = Partial<
  Record<'jira' | 'linear' | 'slack' | 'google', IntegrationHealthEntry>
>;

export async function getIntegrationHealth(): Promise<IntegrationHealthMap> {
  const res = await request<{ health: IntegrationHealthMap }>(
    '/integrations/health',
  );
  return res.health;
}

export function getIntegrationConnectUrl(
  provider: string
): Promise<{ url: string }> {
  return request<{ url: string }>(`/integrations/${provider}/connect-url`);
}

export function disconnectIntegration(provider: string): Promise<void> {
  return request<void>(`/integrations/${provider}`, { method: 'DELETE' });
}

export function syncIntegration(provider: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/integrations/${provider}/sync`, {
    method: 'POST',
  });
}

export function updateIntegrationSchedule(
  provider: string,
  syncSchedule: string,
): Promise<{ provider: string; syncSchedule: string }> {
  return request<{ provider: string; syncSchedule: string }>(
    `/integrations/${provider}/schedule`,
    { method: 'PATCH', body: JSON.stringify({ syncSchedule }) },
  );
}

export function cancelSync(provider: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/integrations/${provider}/cancel-sync`, {
    method: 'POST',
  });
}

export function oauthCallback(
  provider: string,
  code: string,
  state: string
): Promise<{ provider: string; status: string; healthVerified: boolean | null }> {
  return request<{ provider: string; status: string; healthVerified: boolean | null }>(
    `/integrations/${provider}/callback`,
    { method: 'POST', body: JSON.stringify({ code, state }) }
  );
}

export async function getIntegrationResources(provider: string, q?: string): Promise<IntegrationResource[]> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  const res = await request<{ resources: IntegrationResource[] }>(`/integrations/${provider}/resources${qs}`);
  return res.resources;
}

export function testJiraDCConnection(
  instanceUrl: string,
  email: string,
  apiToken: string,
): Promise<{ valid: boolean; displayName?: string; emailAddress?: string }> {
  return request('/integrations/jira-dc/test', {
    method: 'POST',
    body: JSON.stringify({ instanceUrl, email, apiToken }),
  });
}

export function connectJiraDC(
  instanceUrl: string,
  email: string,
  apiToken: string,
): Promise<{ provider: string; jiraType: string; status: string; instanceName: string }> {
  return request('/integrations/jira-dc/connect', {
    method: 'POST',
    body: JSON.stringify({ instanceUrl, email, apiToken }),
  });
}

export function addProjectLink(
  projectId: string,
  data: {
    provider: string;
    externalResourceId: string;
    displayName: string;
    webUrl?: string;
    syncEnabled?: boolean;
    resourceType?: string;
  }
): Promise<ProjectLink> {
  return request<ProjectLink>(`/projects/${projectId}/links`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function removeProjectLink(projectId: string, linkId: string): Promise<void> {
  return request<void>(`/projects/${projectId}/links/${linkId}`, { method: 'DELETE' });
}

export interface TicketStats {
  total: number;
  complete: number;
  inProgress: number;
  inProgressNearDue: number;
  inProgressPastDue: number;
  todo: number;
  todoNearDue: number;
  todoPastDue: number;
  nearDue: { ticketId: string; title: string; dueDate: string }[];
  pastDue: { ticketId: string; title: string; dueDate: string }[];
}

export function getTicketStats(projectId: string): Promise<TicketStats> {
  return request<TicketStats>(`/projects/${projectId}/ticket-stats`);
}

export function updateProjectBriefSettings(
  projectId: string,
  data: { morningBriefEnabled?: boolean; briefDetailLevel?: 'summary' | 'detailed' }
): Promise<void> {
  return request(`/projects/${projectId}/brief-settings`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getPersonalBriefPref(projectId: string): Promise<boolean> {
  const res = await request<{ personalBriefEnabled: boolean }>(`/projects/${projectId}/personal-brief`);
  return res.personalBriefEnabled;
}

export function updatePersonalBriefPref(projectId: string, enabled: boolean): Promise<void> {
  return request(`/projects/${projectId}/personal-brief`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
}

/** Looks up a pending invite for the caller's email without requiring entityId claims.
 *  Used by the NoAccessScreen to auto-detect and redirect invited users. */
export async function getPendingInviteForMe(): Promise<{ inviteId: string; entityId: string } | null> {
  const res = await request<{ invite: { inviteId: string; entityId: string } | null }>('/invites/pending-for-me');
  return res.invite;
}

// Personal integrations (per-user, not entity-level)
export async function listPersonalIntegrations(): Promise<PersonalIntegration[]> {
  const res = await request<{ integrations: PersonalIntegration[] }>('/integrations/personal');
  return res.integrations;
}

export function getPersonalGoogleConnectUrl(): Promise<{ url: string }> {
  return request<{ url: string }>('/integrations/personal/google/connect-url');
}

export function personalOauthCallback(
  provider: string,
  code: string,
  state: string
): Promise<{ provider: string; status: string; email?: string }> {
  return request<{ provider: string; status: string; email?: string }>(
    `/integrations/personal/${provider}/callback`,
    { method: 'POST', body: JSON.stringify({ code, state }) }
  );
}

export function disconnectPersonalGoogle(): Promise<{ provider: string; status: string; revokedAtGoogle?: boolean; artifactsDeleted?: number }> {
  return request<{ provider: string; status: string; revokedAtGoogle?: boolean; artifactsDeleted?: number }>('/integrations/personal/google', {
    method: 'DELETE',
  });
}

export type GoogleVisibilityLevel = 'personal' | 'project_signals' | 'full';

export function setPersonalGoogleVisibility(
  visibilityLevel: GoogleVisibilityLevel
): Promise<{ provider: string; visibilityLevel: GoogleVisibilityLevel }> {
  return request<{ provider: string; visibilityLevel: GoogleVisibilityLevel }>(
    '/integrations/personal/google/visibility',
    { method: 'PUT', body: JSON.stringify({ visibilityLevel }) }
  );
}

export interface IngestedContentItem {
  source: 'calendar';
  projectId: string;
  projectName: string;
  title: string;
  startTime: string | null;
  ingestedAt: string | null;
  hasTranscript: boolean;
  relevanceReason?: string | null;
}

export async function getPersonalGoogleIngestedContent(): Promise<IngestedContentItem[]> {
  const res = await request<{ items: IngestedContentItem[]; total: number }>(
    '/integrations/personal/google/ingested-content'
  );
  return res.items;
}

// Actions
export function executeAction(data: {
  type: string;
  payload: Record<string, unknown>;
  projectId?: string;
  ticketId?: string;
  actionId?: string;
}): Promise<ActionResult> {
  return request<ActionResult>('/actions/execute', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getActionStats(): Promise<{
  total: number;
  byType: Record<string, { total: number; succeeded: number; failed: number }>;
  recentFailures: Array<{
    actionType: string;
    projectId: string | null;
    message: string | null;
    timestamp: string | null;
  }>;
}> {
  return request('/actions/stats');
}
